/**
 * Formatter Utilities
 * Common formatting functions for currency, dates, etc.
 */

import moment from 'moment';
import { formatHotelDate, getHotelDateFormat } from '../../utils/dateUtils';

/**
 * Format number as currency (Indian Rupees)
 * 
 * @param {number} value - Amount to format
 * @param {string} currency - Currency symbol (default: '₹')
 * @returns {string} Formatted currency string
 * 
 * @example
 * formatCurrency(1234.56) // '₹1234.56'
 */
export const formatCurrency = (value, currency = '₹') => {
  if (value === null || value === undefined || isNaN(value)) {
    return `${currency}0.00`;
  }
  return `${currency}${parseFloat(value).toFixed(2)}`;
};

/**
 * Format date using moment.js
 * 
 * @param {moment.Moment|Date|string} date - Date to format
 * @param {string} format - Moment format string (default: 'DD MMM YYYY')
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDate(moment(), 'DD MMM YYYY') // '25 Dec 2024'
 */
export const formatDate = (date, format) => {
  if (!date) return '';
  const displayFormat = format || getHotelDateFormat();
  if (moment.isMoment(date)) {
    return date.format(displayFormat);
  }
  return formatHotelDate(date, displayFormat);
};

/**
 * Format phone number
 * 
 * @param {string} phone - Phone number string
 * @returns {string} Formatted phone number
 * 
 * @example
 * formatPhone('1234567890') // '(123) 456-7890'
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
};

/**
 * Format percentage
 * 
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 * 
 * @example
 * formatPercentage(0.05) // '5.00%'
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00%';
  }
  return `${(parseFloat(value) * 100).toFixed(decimals)}%`;
};

export default {
  formatCurrency,
  formatDate,
  formatPhone,
  formatPercentage,
};

